import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Search, Menu, X, ChevronDown, Flame, Coins } from "lucide-react";
import { api, formatNumber } from "@/lib/api";
import { toast } from "sonner";

const ADMIN_SECRET = "Kadiriisthebossanditinsepolia";

function SearchInline({ compact = false, dark = false }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    const raw = q.trim();
    if (!raw) return;
    if (raw === ADMIN_SECRET) {
      setQ("");
      nav("/admin/login");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.get(`/search`, { params: { q: raw } });
      if (data.type === "tx") nav(`/tx/${data.value}`);
      else if (data.type === "block") nav(`/block/${data.value}`);
      else if (data.type === "address") nav(`/address/${data.value}`);
      else toast.error("No matching block, transaction, or address found");
    } catch {
      toast.error("Search failed");
    } finally {
      setBusy(false);
      setQ("");
    }
  };

  const inputCls = dark
    ? "ks-search-input"
    : "ks-search-input";
  return (
    <form onSubmit={submit} className="relative w-full" data-testid="global-search-form">
      <input
        data-testid="global-search-input"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search by Address / Txn Hash / Block / Token"
        className={inputCls}
        style={compact
          ? { padding: "9px 40px 9px 14px", background: "#f7f9fc", border: "1px solid #dfe4ec", color: "var(--ks-text)" }
          : undefined
        }
      />
      <button
        type="submit"
        data-testid="global-search-submit"
        disabled={busy}
        className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 grid place-items-center rounded-md bg-[var(--ks-primary)] text-white hover:bg-[var(--ks-link-hover)] transition"
        aria-label="Search"
      >
        <Search size={16} />
      </button>
    </form>
  );
}

function TopTicker() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/stats");
        if (alive) setStats(data);
      } catch {/* ignore */}
    };
    load();
    const iv = setInterval(load, 20000);
    return () => { alive = false; clearInterval(iv); };
  }, []);
  return (
    <div className="ks-topbar hidden md:block">
      <div className="max-w-[1280px] mx-auto px-4 lg:px-6 h-9 flex items-center gap-6">
        <span className="flex items-center gap-1.5">
          <Coins size={12} className="text-[var(--ks-primary)]" />
          ETH Price: <span className="text-[var(--ks-text)] font-medium">${stats ? formatNumber(stats.eth_price_usd) : "—"}</span>
          <span className={stats && stats.eth_price_change_pct >= 0 ? "text-[var(--ks-success)]" : "text-[var(--ks-failed)]"}>
            ({stats ? `${stats.eth_price_change_pct >= 0 ? "+" : ""}${stats.eth_price_change_pct}%` : "—"})
          </span>
        </span>
        <span className="flex items-center gap-1.5">
          <Flame size={12} className="text-[var(--ks-pending)]" />
          Gas: <span className="text-[var(--ks-text)] font-medium">{stats ? `${stats.gas_price_gwei} Gwei` : "—"}</span>
        </span>
      </div>
    </div>
  );
}

export default function Header({ showHeroSearch = false }) {
  const [open, setOpen] = useState(false);
  const linkClass = ({ isActive }) => `ks-header-link ${isActive ? "active" : ""}`;
  return (
    <>
      <TopTicker />
      <header className="ks-header sticky top-0 z-40">
        <div className="max-w-[1280px] mx-auto flex items-center gap-6 px-4 lg:px-6 h-16">
          <NavLink to="/" className="flex items-center" data-testid="brand-link">
            <img
              src="/img/etherscan-logo.svg"
              alt="Etherscan"
              className="h-7 w-auto"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.outerHTML = '<span class="font-semibold tracking-tight text-[19px]">Etherscan</span>';
              }}
            />
          </NavLink>

          <nav className="hidden lg:flex items-center gap-1 ml-6">
            <NavLink to="/" end className={linkClass} data-testid="nav-home">Home</NavLink>
            <NavLink to="/blocks" className={linkClass} data-testid="nav-blocks">Blockchain</NavLink>
            <NavLink to="/txs" className={linkClass} data-testid="nav-txs">Transactions</NavLink>
            <NavLink to="/tokens" className={linkClass} data-testid="nav-tokens">Tokens</NavLink>
            <div className="relative group">
              <button className="ks-header-link flex items-center gap-1" data-testid="nav-nfts">NFTs <ChevronDown size={12}/></button>
              <div className="hidden group-hover:block absolute top-full left-0 bg-white border border-[var(--ks-border)] rounded-md shadow-lg py-2 min-w-[200px] z-50">
                <NavLink to="/nfts" className="block px-4 py-2 text-sm hover:bg-[var(--ks-surface)]" data-testid="dd-nft-top">Top NFTs</NavLink>
                <NavLink to="/nfts" className="block px-4 py-2 text-sm hover:bg-[var(--ks-surface)]" data-testid="dd-nft-transfers">NFT Transfers</NavLink>
                <NavLink to="/nfts" className="block px-4 py-2 text-sm hover:bg-[var(--ks-surface)]" data-testid="dd-nft-trades">Latest Trades</NavLink>
              </div>
            </div>
            <div className="relative group">
              <button className="ks-header-link flex items-center gap-1" data-testid="nav-resources">Resources <ChevronDown size={12}/></button>
              <div className="hidden group-hover:block absolute top-full left-0 bg-white border border-[var(--ks-border)] rounded-md shadow-lg py-2 min-w-[200px] z-50">
                <NavLink to="/api-docs" className="block px-4 py-2 text-sm hover:bg-[var(--ks-surface)]" data-testid="dd-api-docs">API Documentation</NavLink>
                <NavLink to="/charts" className="block px-4 py-2 text-sm hover:bg-[var(--ks-surface)]" data-testid="dd-charts">Charts &amp; Stats</NavLink>
                <NavLink to="/accounts" className="block px-4 py-2 text-sm hover:bg-[var(--ks-surface)]" data-testid="dd-accounts">Top Accounts</NavLink>
              </div>
            </div>
            <div className="relative group">
              <button className="ks-header-link flex items-center gap-1" data-testid="nav-developers">Developers <ChevronDown size={12}/></button>
              <div className="hidden group-hover:block absolute top-full left-0 bg-white border border-[var(--ks-border)] rounded-md shadow-lg py-2 min-w-[200px] z-50">
                <NavLink to="/api-docs" className="block px-4 py-2 text-sm hover:bg-[var(--ks-surface)]" data-testid="dd-dev-apidocs">API Docs</NavLink>
                <NavLink to="/rpc" className="block px-4 py-2 text-sm hover:bg-[var(--ks-surface)]" data-testid="dd-dev-rpc">RPC Endpoint</NavLink>
                <NavLink to="/verify-contract" className="block px-4 py-2 text-sm hover:bg-[var(--ks-surface)]" data-testid="dd-dev-verify">Verify Contract</NavLink>
                <NavLink to="/verify-contract" className="block px-4 py-2 text-sm hover:bg-[var(--ks-surface)]" data-testid="dd-dev-bytecode">Byte-code Verifier</NavLink>
              </div>
            </div>
            <div className="relative group">
              <button className="ks-header-link flex items-center gap-1" data-testid="nav-more">More <ChevronDown size={12}/></button>
              <div className="hidden group-hover:block absolute top-full right-0 bg-white border border-[var(--ks-border)] rounded-md shadow-lg py-2 min-w-[180px] z-50">
                <NavLink to="/admin/login" className="block px-4 py-2 text-sm hover:bg-[var(--ks-surface)]" data-testid="dd-more-admin">Admin Panel</NavLink>
                <NavLink to="/tokens" className="block px-4 py-2 text-sm hover:bg-[var(--ks-surface)]" data-testid="dd-more-tokens">Tokens</NavLink>
                <NavLink to="/contracts" className="block px-4 py-2 text-sm hover:bg-[var(--ks-surface)]" data-testid="dd-more-contracts">Verified Contracts</NavLink>
                <NavLink to="/newsletter" className="block px-4 py-2 text-sm hover:bg-[var(--ks-surface)]" data-testid="dd-more-newsletter">Newsletter</NavLink>
              </div>
            </div>
            <NavLink to="/admin/login" className="ks-header-link border-l border-[var(--ks-border)] ml-2 pl-3" data-testid="nav-signin">Sign In</NavLink>
          </nav>

          {!showHeroSearch && (
            <div className="hidden md:block ml-auto w-[380px] max-w-[42vw]">
              <SearchInline compact />
            </div>
          )}

          <button
            className="ml-auto lg:hidden p-2"
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            aria-label="menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
        {open && (
          <div className="lg:hidden border-t border-[var(--ks-border)] px-4 py-3 space-y-2 bg-white">
            <SearchInline compact />
            <NavLink onClick={() => setOpen(false)} to="/" end className="block ks-header-link" data-testid="mnav-home">Home</NavLink>
            <NavLink onClick={() => setOpen(false)} to="/blocks" className="block ks-header-link" data-testid="mnav-blocks">Blocks</NavLink>
            <NavLink onClick={() => setOpen(false)} to="/txs" className="block ks-header-link" data-testid="mnav-txs">Transactions</NavLink>
          </div>
        )}
      </header>
      {showHeroSearch && (
        <section className="ks-hero">
          <div className="max-w-[1280px] mx-auto px-4 lg:px-6 py-14">
            <h1 className="text-white text-xl md:text-[24px] font-semibold tracking-tight" data-testid="hero-title">
              The Etherscan Blockchain Explorer
            </h1>
            <div className="mt-6 max-w-3xl flex items-stretch gap-0 bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="hidden md:flex items-center px-4 border-r border-[var(--ks-border)] bg-[#f7f9fc]">
                <select className="bg-transparent text-sm text-[var(--ks-text)] outline-none py-3 pr-2" data-testid="hero-filter">
                  <option>All Filters</option>
                  <option>Addresses</option>
                  <option>Tokens</option>
                  <option>Blocks</option>
                </select>
              </div>
              <div className="flex-1 relative">
                <SearchInline />
              </div>
            </div>
            <div className="mt-4 text-xs text-white/85 flex items-center gap-2 bg-black/25 px-3 py-2 rounded max-w-3xl">
              <span className="font-semibold">Sponsored:</span>
              <span>🔥 Etherscan is live — explore ERC-20 transfers on-chain.</span>
            </div>
          </div>
        </section>
      )}
    </>
  );
}
